/**
 * MenuGalleryWrapper - Premium Fullscreen Menu Viewer
 * 
 * Complete rewrite for optimal menu viewing:
 * 
 * ZOOM SUPPORT:
 * - Mobile: Pinch-to-zoom (two-finger gesture) + double-tap to zoom
 * - Desktop: Mouse wheel zoom + click-drag pan
 * - Buttons: +/- zoom controls for all devices
 * 
 * NAVIGATION:
 * - Swipe left/right to change pages (only at zoom=1)
 * - Arrow keys (desktop)
 * - Nav buttons
 * - Page indicator dots
 * 
 * PERFORMANCE:
 * - No CSS transitions during active gestures (zero lag)
 * - requestAnimationFrame for smooth transform updates
 * - Preloads adjacent images
 * - Will-change hints for GPU compositing
 * 
 * COPY PROTECTION:
 * - Context menu blocked, drag blocked
 * - But touch/pointer events are NOT blocked (no ::after overlay)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize } from 'lucide-react';

interface MenuImage {
  id?: string | number;
  path: string;
  [key: string]: any;
}

interface MenuGalleryWrapperProps {
  menuImages: MenuImage[];
  restaurantName: string;
  isOpen: boolean;
  onClose: () => void;
}

// ── Clamp helper ────────────────────────
const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

// ── Distance between two touch points ───
const getTouchDist = (t: React.TouchEvent | TouchEvent) => {
  const a = t.touches[0];
  const b = t.touches[1];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
};

const getTouchCenter = (t: React.TouchEvent | TouchEvent) => {
  const a = t.touches[0];
  const b = t.touches[1];
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

export const MenuGalleryWrapper: React.FC<MenuGalleryWrapperProps> = ({
  menuImages,
  restaurantName,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Transform state (no React state for hot-path — use refs for zero-lag)
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const [, forceUpdate] = useState(0);
  const rerender = useCallback(() => forceUpdate(n => n + 1), []);

  // Touch tracking refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const gestureActive = useRef(false);
  const lastTouchDist = useRef(0);
  const lastTouchCenter = useRef({ x: 0, y: 0 });
  const lastTapTime = useRef(0);
  
  // Single-finger swipe tracking
  const swipeStartRef = useRef({ x: 0, y: 0, time: 0 });
  const isSwiping = useRef(false);

  // Mouse drag tracking
  const mouseDragRef = useRef({ active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });

  // Auto-hide controls
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Apply transform (direct DOM, no React render) ─────
  const applyTransform = useCallback(() => {
    if (!imgRef.current) return;
    const z = zoomRef.current;
    const { x, y } = panRef.current;
    imgRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${z})`;
  }, []);

  // ── Constrain pan within boundaries ───────────────────
  const constrainPan = useCallback(() => {
    if (!imgRef.current || !containerRef.current) return;
    const z = zoomRef.current;
    if (z <= 1) {
      panRef.current = { x: 0, y: 0 };
      return;
    }
    const img = imgRef.current.getBoundingClientRect();
    const box = containerRef.current.getBoundingClientRect();

    // How much the scaled image exceeds the container
    const overflowX = Math.max(0, (img.width * z / zoomRef.current - box.width) / 2);
    const overflowY = Math.max(0, (img.height * z / zoomRef.current - box.height) / 2);

    panRef.current = {
      x: clamp(panRef.current.x, -overflowX, overflowX),
      y: clamp(panRef.current.y, -overflowY, overflowY),
    };
  }, []);

  // ── Reset transform to default ────────────────────────
  const resetTransform = useCallback(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    if (imgRef.current) {
      imgRef.current.style.transition = 'transform 0.3s cubic-bezier(0.2,0,0,1)';
      applyTransform();
      setTimeout(() => {
        if (imgRef.current) imgRef.current.style.transition = '';
      }, 320);
    }
    rerender();
  }, [applyTransform, rerender]);

  // ── Zoom to specific level at a point ─────────────────
  const zoomTo = useCallback((newZoom: number, centerX?: number, centerY?: number) => {
    const oldZoom = zoomRef.current;
    const z = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
    
    if (centerX !== undefined && centerY !== undefined && containerRef.current) {
      const box = containerRef.current.getBoundingClientRect();
      const cx = centerX - box.left - box.width / 2;
      const cy = centerY - box.top - box.height / 2;
      
      const factor = z / oldZoom;
      panRef.current = {
        x: cx - factor * (cx - panRef.current.x),
        y: cy - factor * (cy - panRef.current.y),
      };
    }
    
    zoomRef.current = z;
    if (z <= 1) panRef.current = { x: 0, y: 0 };
    else constrainPan();
    
    if (imgRef.current) {
      imgRef.current.style.transition = 'transform 0.25s cubic-bezier(0.2,0,0,1)';
      applyTransform();
      setTimeout(() => {
        if (imgRef.current) imgRef.current.style.transition = '';
      }, 260);
    }
    rerender();
  }, [applyTransform, constrainPan, rerender]);

  // ── Touch handlers ────────────────────────────────────
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      gestureActive.current = true;
      lastTouchDist.current = getTouchDist(e);
      lastTouchCenter.current = getTouchCenter(e);
      if (imgRef.current) imgRef.current.style.transition = '';
    } else if (e.touches.length === 1) {
      // Single finger — potential swipe or pan
      const touch = e.touches[0];
      swipeStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      isSwiping.current = zoomRef.current <= 1;
      
      if (zoomRef.current > 1) {
        // Pan mode
        gestureActive.current = true;
        lastTouchCenter.current = { x: touch.clientX, y: touch.clientY };
        if (imgRef.current) imgRef.current.style.transition = '';
      }
      
      // Double-tap detection
      const now = Date.now();
      if (now - lastTapTime.current < 300) {
        e.preventDefault();
        if (zoomRef.current > 1.1) {
          resetTransform();
        } else {
          zoomTo(2.5, touch.clientX, touch.clientY);
        }
        lastTapTime.current = 0;
      } else {
        lastTapTime.current = now;
      }
    }
  }, [resetTransform, zoomTo]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      const dist = getTouchDist(e);
      const center = getTouchCenter(e);
      
      if (lastTouchDist.current > 0) {
        const scaleDelta = dist / lastTouchDist.current;
        const newZoom = clamp(zoomRef.current * scaleDelta, MIN_ZOOM, MAX_ZOOM);
        
        // Zoom toward pinch center
        if (containerRef.current) {
          const box = containerRef.current.getBoundingClientRect();
          const cx = center.x - box.left - box.width / 2;
          const cy = center.y - box.top - box.height / 2;
          
          const factor = newZoom / zoomRef.current;
          panRef.current = {
            x: cx - factor * (cx - panRef.current.x),
            y: cy - factor * (cy - panRef.current.y),
          };
        }
        
        // Also pan with two-finger drag
        panRef.current.x += center.x - lastTouchCenter.current.x;
        panRef.current.y += center.y - lastTouchCenter.current.y;
        
        zoomRef.current = newZoom;
        if (newZoom <= 1) panRef.current = { x: 0, y: 0 };
        applyTransform();
      }
      
      lastTouchDist.current = dist;
      lastTouchCenter.current = center;
    } else if (e.touches.length === 1 && zoomRef.current > 1) {
      // Single finger pan when zoomed
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - lastTouchCenter.current.x;
      const dy = touch.clientY - lastTouchCenter.current.y;
      
      panRef.current.x += dx;
      panRef.current.y += dy;
      constrainPan();
      applyTransform();
      
      lastTouchCenter.current = { x: touch.clientX, y: touch.clientY };
      isSwiping.current = false;
    }
  }, [applyTransform, constrainPan]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 0) {
      // Swipe detection (only at zoom = 1)
      if (isSwiping.current && zoomRef.current <= 1 && menuImages.length > 1) {
        const dx = (e.changedTouches[0]?.clientX ?? 0) - swipeStartRef.current.x;
        const dt = Date.now() - swipeStartRef.current.time;
        
        if (Math.abs(dx) > 60 && dt < 400) {
          if (dx < 0) {
            setCurrentIndex(prev => prev === menuImages.length - 1 ? 0 : prev + 1);
          } else {
            setCurrentIndex(prev => prev === 0 ? menuImages.length - 1 : prev - 1);
          }
        }
      }
      
      // Snap to min zoom if below threshold
      if (zoomRef.current < 1.05) {
        resetTransform();
      } else {
        constrainPan();
        if (imgRef.current) {
          imgRef.current.style.transition = 'transform 0.2s ease-out';
          applyTransform();
          setTimeout(() => {
            if (imgRef.current) imgRef.current.style.transition = '';
          }, 220);
        }
      }
      
      gestureActive.current = false;
      isSwiping.current = false;
      lastTouchDist.current = 0;
      rerender();
    }
  }, [menuImages.length, applyTransform, constrainPan, resetTransform, rerender]);

  // ── Mouse wheel zoom (desktop) ───────────────────────
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.003;
    const newZoom = clamp(zoomRef.current * (1 + delta), MIN_ZOOM, MAX_ZOOM);
    
    // Zoom toward cursor position
    if (containerRef.current) {
      const box = containerRef.current.getBoundingClientRect();
      const cx = e.clientX - box.left - box.width / 2;
      const cy = e.clientY - box.top - box.height / 2;
      
      const factor = newZoom / zoomRef.current;
      panRef.current = {
        x: cx - factor * (cx - panRef.current.x),
        y: cy - factor * (cy - panRef.current.y),
      };
    }
    
    zoomRef.current = newZoom;
    if (newZoom <= 1) panRef.current = { x: 0, y: 0 };
    else constrainPan();
    applyTransform();
    rerender();
  }, [applyTransform, constrainPan, rerender]);

  // ── Mouse drag pan (desktop) ──────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0 || zoomRef.current <= 1) return;
    e.preventDefault();
    mouseDragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startPanX: panRef.current.x,
      startPanY: panRef.current.y,
    };
    if (imgRef.current) imgRef.current.style.transition = '';
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!mouseDragRef.current.active) return;
      panRef.current = {
        x: mouseDragRef.current.startPanX + (e.clientX - mouseDragRef.current.startX),
        y: mouseDragRef.current.startPanY + (e.clientY - mouseDragRef.current.startY),
      };
      constrainPan();
      applyTransform();
    };
    
    const handleMouseUp = () => {
      if (!mouseDragRef.current.active) return;
      mouseDragRef.current.active = false;
      if (containerRef.current) containerRef.current.style.cursor = '';
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isOpen, applyTransform, constrainPan]);

  // ── Reset on page change ──────────────────────────────
  useEffect(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    if (imgRef.current) {
      imgRef.current.style.transition = '';
      imgRef.current.style.transform = 'translate3d(0,0,0) scale(1)';
    }
    rerender();
  }, [currentIndex, rerender]);

  // ── Reset on open ─────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      zoomRef.current = 1;
      panRef.current = { x: 0, y: 0 };
      setControlsVisible(true);
      rerender();
    }
  }, [isOpen, rerender]);

  // ── Body scroll lock ──────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
    return undefined;
  }, [isOpen]);

  // ── Keyboard navigation ───────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape': onClose(); break;
        case 'ArrowLeft': navigate(-1); break;
        case 'ArrowRight': navigate(1); break;
        case '+':
        case '=': zoomTo(zoomRef.current + 0.5); break;
        case '-': zoomTo(zoomRef.current - 0.5); break;
        case '0': resetTransform(); break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, zoomTo, resetTransform]);

  // ── Navigate pages ────────────────────────────────────
  const navigate = useCallback((dir: number) => {
    setCurrentIndex(prev => {
      if (dir > 0) return prev === menuImages.length - 1 ? 0 : prev + 1;
      return prev === 0 ? menuImages.length - 1 : prev - 1;
    });
  }, [menuImages.length]);

  // ── Toggle controls visibility ────────────────────────
  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => {
      if (zoomRef.current > 1) setControlsVisible(false);
    }, 3000);
  }, []);

  // ── Preload adjacent images ───────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const preload = (idx: number) => {
      if (idx >= 0 && idx < menuImages.length) {
        const img = new Image();
        img.src = menuImages[idx].path;
      }
    };
    preload(currentIndex + 1);
    preload(currentIndex - 1);
  }, [isOpen, currentIndex, menuImages]);

  if (!isOpen || menuImages.length === 0) return null;

  const currentImage = menuImages[currentIndex];
  const currentZoom = zoomRef.current;
  const isZoomed = currentZoom > 1.05;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex flex-col select-none"
      style={{ backgroundColor: 'rgba(0,0,0,0.97)' }}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => {
        // Close on backdrop click only when not zoomed
        if (e.target === e.currentTarget && !isZoomed) onClose();
      }}
    >
      {/* ── Header ─────────────────────────────────────── */}
      <div 
        className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-3 py-2 sm:px-4 sm:py-3 transition-opacity duration-300"
        style={{ 
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)',
          opacity: controlsVisible ? 1 : 0,
          pointerEvents: controlsVisible ? 'auto' : 'none',
          paddingTop: 'max(8px, env(safe-area-inset-top, 0px))',
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={onClose}
            className="text-white p-2 rounded-full hover:bg-white/15 active:bg-white/25 transition-colors flex-shrink-0"
            aria-label="Schließen"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
          </button>
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-semibold text-sm sm:text-base truncate">
              {restaurantName}
            </h3>
            <p className="text-white/60 text-xs">
              Seite {currentIndex + 1} von {menuImages.length}
            </p>
          </div>
        </div>

        {/* Zoom controls */}
        <div 
          className="flex items-center gap-0.5 flex-shrink-0 rounded-full px-1 py-1"
          style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}
        >
          <button
            onClick={() => zoomTo(zoomRef.current - 0.5)}
            disabled={currentZoom <= MIN_ZOOM}
            className="text-white p-2 rounded-full hover:bg-white/15 active:bg-white/25 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            aria-label="Verkleinern"
          >
            <ZoomOut className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2} />
          </button>
          
          {isZoomed && (
            <span className="text-white/80 text-xs font-mono px-1 min-w-[2.5rem] text-center">
              {Math.round(currentZoom * 100)}%
            </span>
          )}
          
          <button
            onClick={() => zoomTo(zoomRef.current + 0.5)}
            disabled={currentZoom >= MAX_ZOOM}
            className="text-white p-2 rounded-full hover:bg-white/15 active:bg-white/25 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            aria-label="Vergrößern"
          >
            <ZoomIn className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2} />
          </button>
          
          {isZoomed && (
            <button
              onClick={resetTransform}
              className="text-white p-2 rounded-full hover:bg-white/15 active:bg-white/25 transition-colors"
              aria-label="Zurücksetzen"
            >
              <Maximize className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* ── Image Container (gesture surface) ──────────── */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden"
        style={{ 
          touchAction: 'none',
          cursor: isZoomed ? 'grab' : 'default',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onClick={(e) => {
          // Toggle controls on tap (when not zoomed and not dragging)
          if (!mouseDragRef.current.active && e.target !== e.currentTarget) {
            showControls();
          }
          if (e.target === e.currentTarget && !isZoomed) onClose();
        }}
      >
        <img
          ref={imgRef}
          src={currentImage.path}
          alt={`${restaurantName} Speisekarte Seite ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
          style={{
            willChange: 'transform',
            transformOrigin: 'center center',
            transform: 'translate3d(0,0,0) scale(1)',
            // Copy protection without blocking touch events
            WebkitUserSelect: 'none',
            userSelect: 'none',
            pointerEvents: 'auto',
          }}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          loading="eager"
        />
      </div>

      {/* ── Zoom hint (appears briefly on open) ────────── */}
      {!isZoomed && (
        <div 
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-40 pointer-events-none animate-fade-in"
          style={{ animationDelay: '0.5s', animationFillMode: 'both' }}
        >
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-full text-white/70 text-xs"
            style={{ 
              background: 'rgba(0,0,0,0.5)', 
              backdropFilter: 'blur(8px)',
            }}
          >
            <ZoomIn className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="hidden sm:inline">Scrollen zum Zoomen · Doppelklick für 2.5×</span>
            <span className="sm:hidden">Pinch zum Zoomen · Doppeltippen für 2.5×</span>
          </div>
        </div>
      )}

      {/* ── Navigation Arrows (desktop, hidden when zoomed) ── */}
      {menuImages.length > 1 && !isZoomed && (
        <>
          <button
            onClick={() => navigate(-1)}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center active:scale-90 z-40 transition-all"
            style={{ 
              background: 'rgba(0,0,0,0.5)', 
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              opacity: controlsVisible ? 1 : 0,
              pointerEvents: controlsVisible ? 'auto' : 'none',
            }}
            aria-label="Vorherige Seite"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-white" strokeWidth={2} />
          </button>
          <button
            onClick={() => navigate(1)}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center active:scale-90 z-40 transition-all"
            style={{ 
              background: 'rgba(0,0,0,0.5)', 
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              opacity: controlsVisible ? 1 : 0,
              pointerEvents: controlsVisible ? 'auto' : 'none',
            }}
            aria-label="Nächste Seite"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-white" strokeWidth={2} />
          </button>
        </>
      )}

      {/* ── Page Indicators ────────────────────────────── */}
      {menuImages.length > 1 && (
        <div 
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-40 py-2 px-3 rounded-full transition-opacity duration-300"
          style={{ 
            background: 'rgba(0,0,0,0.4)', 
            backdropFilter: 'blur(8px)',
            opacity: controlsVisible ? 1 : 0.6,
          }}
        >
          {menuImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className="rounded-full transition-all duration-200"
              style={{
                width: index === currentIndex ? '22px' : '7px',
                height: '7px',
                background: index === currentIndex ? '#18B66A' : 'rgba(255,255,255,0.4)',
              }}
              aria-label={`Seite ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default MenuGalleryWrapper;
